/**
 * ULD统计分析主页面
 * 聚焦三个核心业务维度：机场维度、航班维度、长时间无动态ULD
 */

import React, { useState, useEffect, useCallback } from "react";
import { Card, Row, Col, Space, Typography, message, Spin, Alert, Button, Statistic, Tag, Divider } from "antd";
import { BarChartOutlined, ReloadOutlined, DownloadOutlined, AppstoreOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, BankOutlined, CarOutlined, ClockCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { uldService } from "../../../../api/enhancedApi";
import UldLocationStatTable from "./UldLocationStatTable";
import UldInactiveTable from "./UldInactiveTable";
import UldFlightTable from "./UldFlightTable";
import PermissionGuard from "../../../../components/auth/PermissionGuard";
import { FilteredExportButton } from "../../../../components/export";

const { Title, Text } = Typography;

const UldStatisticsPage = () => {
    const [loading, setLoading] = useState(false);
    const [statisticsData, setStatisticsData] = useState(null);
    const [lastFetchedAt, setLastFetchedAt] = useState(null);
    const [refreshNonce, setRefreshNonce] = useState(0);
    const [locationStatFilters, setLocationStatFilters] = useState({});
    const [inactiveFilters, setInactiveFilters] = useState({ inactive_days: 7 });

    const fetchStatistics = useCallback(async () => {
        setLoading(true);
        try {
            const response = await uldService.getUldStatistics({});
            setStatisticsData(response.data);
            setLastFetchedAt(dayjs());
        } catch (error) {
            message.error("统计数据获取失败，请稍后重试");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatistics();
    }, [fetchStatistics]);

    const handleRefresh = () => {
        fetchStatistics();
        setRefreshNonce((current) => current + 1);
    };

    // 导出功能现在使用标准的FilteredExportButton组件，不需要自定义处理函数



    // 渲染统计概览卡片（简化版）
    const renderStatisticsCards = () => {
        if (!statisticsData) return null;

        const { total_count, availability_statistics = {}, core_status_statistics = {} } = statisticsData;

        // 顶栏口径按核心状态数：丢失单独一张卡；「不可用数量」只数停用加退役，不含丢失
        // （不能直接用可用性的不可用总数——那里含丢失）
        const lostCount = core_status_statistics["LOST"] || 0;
        const unserviceableExcludingLost =
            (core_status_statistics["OUT_OF_SERVICE"] || 0) + (core_status_statistics["RETIRED"] || 0);

        const cardData = [
            {
                title: "ULD总数",
                note: "全部库存，含丢失",
                value: total_count,
                prefix: <AppstoreOutlined />,
                valueStyle: { color: "#1890ff" },
            },
            {
                title: "可用数量",
                note: "在库，可派",
                value: availability_statistics["AVAILABLE"] || 0,
                prefix: <CheckCircleOutlined />,
                valueStyle: { color: "#52c41a" },
            },
            {
                title: "占用数量",
                note: "在途",
                value: availability_statistics["OCCUPIED"] || 0,
                prefix: <ExclamationCircleOutlined />,
                valueStyle: { color: "#faad14" },
            },
            {
                title: "不可用数量",
                note: "停用+退役，不含丢失",
                value: unserviceableExcludingLost,
                prefix: <CloseCircleOutlined />,
                valueStyle: { color: "#ff4d4f" },
            },
            {
                title: "丢失",
                note: "已确认找不到",
                value: lostCount,
                prefix: <CloseCircleOutlined />,
                valueStyle: { color: "#722ed1" },
            },
        ];

        return (
            <>
            <Alert
                type="info"
                showIcon
                message="顶栏为全局库存（含在途及其他位置），不受下方机场表筛选影响；机场表只统计当前落在机场上的板。"
                style={{ marginBottom: 16 }}
            />
            <Row gutter={16} align="stretch" style={{ marginBottom: "24px" }}>
                {cardData.map((card, index) => (
                    <Col flex="1 1 0" key={index} style={{ display: "flex" }}>
                        <Card
                            size="small"
                            style={{ width: "100%", height: "100%" }}
                            styles={{ body: { height: "100%" } }}
                        >
                            <Statistic
                                title={
                                    <div>
                                        <div>{card.title}</div>
                                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 400, lineHeight: "18px", display: "block", minHeight: 18, whiteSpace: "nowrap" }}>
                                            {card.note}
                                        </Text>
                                    </div>
                                }
                                value={card.value}
                                prefix={card.prefix}
                                suffix={card.suffix}
                                valueStyle={card.valueStyle}
                            />
                        </Card>
                    </Col>
                ))}
            </Row>
            </>
        );
    };

    const renderActions = () => (
        <Card style={{ marginBottom: "24px" }} size="small">
            <Row gutter={16} align="middle">
                <Col span={24}>
                    <Space size="large">
                        <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading} size="small">
                            刷新统计
                        </Button>
                        <Text type="secondary">
                            更新时间: {lastFetchedAt ? lastFetchedAt.format("HH:mm:ss") : "未加载"}
                        </Text>
                    </Space>
                </Col>
            </Row>
        </Card>
    );

    return (
        <div style={{ padding: "24px", backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
            {/* 页面标题 */}
            <div style={{ marginBottom: "24px" }}>
                <Title level={2} style={{ margin: 0 }}>
                    <BarChartOutlined style={{ marginRight: "8px", color: "#1890ff" }} />
                    ULD统计分析
                </Title>
                <Text type="secondary">ULD资产分析：机场维度、航班维度、闲置监控</Text>
            </div>

            {/* 操作区域 */}
            {renderActions()}

            {/* 加载状态 */}
            <Spin spinning={loading}>
                {/* 统计概览卡片 */}
                {renderStatisticsCards()}

                {/* 核心业务维度分析区域 */}
                <Row gutter={[16, 16]}>
                    {/* 机场维度分析 */}
                    <Col span={24}>
                        <Card
                            title={
                                <Space>
                                    <BankOutlined style={{ color: "#52c41a" }} />
                                    <span>机场维度 - ULD站点状态统计</span>
                                </Space>
                            }
                            extra={
                                <Space>
                                    <Tag color="green">站点状态统计</Tag>
                                    <PermissionGuard permission="uld:export">
                                        <FilteredExportButton
                                            resourceType="uld_airport_statistics"
                                            filterParams={locationStatFilters}
                                            buttonText="导出"
                                            buttonProps={{
                                                icon: <DownloadOutlined />,
                                                size: "small",
                                                type: "primary",
                                            }}
                                        />
                                    </PermissionGuard>
                                </Space>
                            }
                        >
                            <UldLocationStatTable refreshTrigger={refreshNonce} onFiltersChange={setLocationStatFilters} />
                        </Card>
                    </Col>

                    {/* 航班维度分析 */}
                    <Col span={24}>
                        <Card
                            title={
                                <Space>
                                    <CarOutlined style={{ color: "#1890ff" }} />
                                    <span>航班维度 - 在途ULD运输统计</span>
                                </Space>
                            }
                            extra={
                                <Space>
                                    <Tag color="blue">运输状态监控</Tag>
                                    <PermissionGuard permission="uld:export">
                                        <FilteredExportButton
                                            resourceType="uld_flight_statistics"
                                            filterParams={{}}
                                            buttonText="导出"
                                            buttonProps={{
                                                icon: <DownloadOutlined />,
                                                size: "small",
                                                type: "primary",
                                            }}
                                        />
                                    </PermissionGuard>
                                </Space>
                            }
                        >
                            <UldFlightTable refreshTrigger={refreshNonce} />
                        </Card>
                    </Col>

                    {/* 长时间无动态ULD监控 */}
                    <Col span={24}>
                        <Card
                            title={
                                <Space>
                                    <ClockCircleOutlined style={{ color: "#faad14" }} />
                                    <span>闲置监控 - 长时间无动态ULD</span>
                                </Space>
                            }
                            extra={
                                <Space>
                                    <Tag color="orange">异常监控</Tag>
                                    <PermissionGuard permission="uld:export">
                                        <FilteredExportButton
                                            resourceType="uld_inactive"
                                            filterParams={inactiveFilters}
                                            buttonText={`导出${inactiveFilters.inactive_days > 0 ? `(${inactiveFilters.inactive_days}天以外)` : ''}`}
                                            buttonProps={{
                                                icon: <DownloadOutlined />,
                                                size: "small",
                                                type: "primary",
                                            }}
                                        />
                                    </PermissionGuard>
                                </Space>
                            }
                        >
                            <UldInactiveTable refreshTrigger={refreshNonce} onFiltersChange={setInactiveFilters} />
                        </Card>
                    </Col>
                </Row>
            </Spin>
        </div>
    );
};

export default UldStatisticsPage;
